import React, {
  useEffect, memo, useState, useCallback, useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import find from 'lodash/find';
import { offerQualifyBuilder, getSessionData } from '../../offerUtils';
import {
  setIsMatchedActiveRefundOffer,
  setActiveRefundToken,
} from '../../offerSlice.js';
import {
  useOptOfferMutation,
  useQualifyOfferMutation,
  useInitOfferTokenByTypeMutation,
} from '../../../../../store/services/common/offers/offerApi.js';
import { useLogTrackMutation } from '../../../../../store/services/common/logTrack/logTrackApi';
import pubSubInstance from '../../../../../shared/utils/pubSub.js';
import { showLoadder, hideLoadder } from '../../../../../store/slices/loadderSlice';

const ActiveRefund = () => {
  const offerRenderer = window.COMMERCEWidget?.offerRenderer;
  const dispatch = useDispatch();
  const {
    entities: {
      activeRefundToken: widgetToken,
      offerInitData: {
        widgetAPIServerHost,
      } = {},
    },
    isMatchedActiveRefundOffer,
  } = useSelector(state => state.offersData);

  const {
    widgetNameSpace,
    locale,
  } = useSelector(state => state.propsData);

  const [ optOffer ] = useOptOfferMutation();
  const [ qualifyOffer ] = useQualifyOfferMutation();
  const [ initOfferTokenByType ] = useInitOfferTokenByTypeMutation();
  const [ logTrack ] = useLogTrackMutation();

  const aRData = getSessionData(`${widgetNameSpace}-checkoutActiveRefund`);
  const defaultARValue = aRData.accept;
  const [ firstOpt, setFirstOpt ] = useState(isEmpty(aRData));
  const [ optionValue, setOptionValue ] = useState(defaultARValue);
  const [ optDataState, setOptDataState ] = useState(undefined);
  const [ renderActiveRefund, setRenderActiveRefund ] = useState(null);
  const [ acceptId, setAcceptID ] = useState(undefined);

  const qualifyActiveRefundOffer = useMemo(() => {
    return offerQualifyBuilder({
      qualifyOffer, offerType: 'active-refund', offer: renderActiveRefund, logTrack,
    });
  }, [ qualifyOffer, renderActiveRefund, logTrack ]);

  const handleOptDataChanged = useCallback((optData) => {
    const { accept, status, response: { products, quoteId } } = optData;
    const storeData = {
      accept,
      status,
      quoteId,
      products,
      activeRefundToken: widgetToken,
    };
    sessionStorage.setItem(`${widgetNameSpace}-checkoutActiveRefund`, JSON.stringify(storeData));
    // First select is not "NO" or option value is changed will call optOffer
    if ((firstOpt && !!accept) || (!firstOpt && accept !== optionValue)) {
      optOffer({
        offerType: 'active-refund',
        params: {
          opted: accept,
          products,
        },
        show: noop,
        hide: noop,
      }).unwrap().then((
        { repriceRequired },
      ) => {
        if (repriceRequired) {
          pubSubInstance.dispatch(`${widgetNameSpace}-reload`);
        }
      }).finally(() => {
        dispatch(hideLoadder());
      });
    }
    setFirstOpt(false);
    setOptionValue(accept);
  }, [ firstOpt, optionValue, widgetToken, optOffer, setFirstOpt, setOptionValue ]);

  const handleActiveRefundOptionChange = (data) => {
    if (isEqual(data, optDataState)) return;
    setOptDataState(data);
  };

  useEffect(() => {
    !isEmpty(optDataState) && handleOptDataChanged(optDataState);
  }, [ optDataState ]);

  const trackingForSetAcceptValue = ({ id }) => {
    if (id === 'commerce.offer.active.refund.accept' || id === 'commerce.offer.active.refund.decline') {
      setAcceptID(id);
    }
  };

  useEffect(() => {
    if (!acceptId) { return; }
    const accept = (acceptId === 'commerce.offer.active.refund.accept');
    // First select is not "NO" or option value is changed will quote data and show loader
    // and hide the loader after quote and optOffer finished
    if ((firstOpt && !!accept) || (!firstOpt && accept !== optionValue)) {
      dispatch(showLoadder());
    }
  }, [ acceptId ]);

  const mountActiveRefund = () => {
    if (!widgetToken || !offerRenderer) {
      return null;
    }
    const activeRefundData = getSessionData(`${widgetNameSpace}-checkoutActiveRefund`);
    let defaultValue = activeRefundData.accept;
    if (defaultValue === true) {
      defaultValue = 'yes';
    } else if (defaultValue === false) {
      defaultValue = 'no';
    }

    const params = {
      defaultValue,
      jwt: widgetToken,
      locale,
      widgetAPIServerHost,
      handleActiveRefundOptionChange,
      trackingCallback: trackingForSetAcceptValue,
      show: noop, // Not use the default show/hide and handle as specific
      hide: noop, // business that First select is not "NO" should not show loading
      initCallback: ({ response: { matchedOffer } }) => {
        dispatch(setIsMatchedActiveRefundOffer(matchedOffer));
      },
    };
    const prop = omitBy(params, isUndefined);

    renderActiveRefund?.destroy(); // For props are from async api, So it should rerender the widget
    return offerRenderer.renderActiveRefundWidget(
      document.getElementById('offer-widget-activeRefund'), prop);
  };

  const resetOfferState = useCallback(() => {
    setFirstOpt(true);
    setOptionValue(undefined);
    setOptDataState(undefined);
    setAcceptID(undefined);
  }, []);

  const reInitActiveRefund = useCallback(() => {
    sessionStorage.removeItem(`${widgetNameSpace}-checkoutActiveRefund`);
    initOfferTokenByType({
      params: { offerTypes: [ 'ACTIVE_REFUND' ] },
    }).unwrap().then((
      { offerData },
    ) => {
      resetOfferState();
      const activeRefundToken = find(offerData, { type: 'ACTIVE_REFUND' })?.widgetToken;
      dispatch(setActiveRefundToken(activeRefundToken));
    });
  }, [ widgetNameSpace, initOfferTokenByType, resetOfferState ]);

  const handleQualify = useCallback((paymentData) => {
    return new Promise((resolve) => {
      const { accept, response: { products, quoteId } = {} } = optDataState || {};
      if (!isMatchedActiveRefundOffer || !accept) {
        resolve({ status: true });
      } else {
        qualifyActiveRefundOffer({
          params: {
            ...paymentData,
            products,
          },
          quoteId,
        }).then(({ status }) => {
          if (status) {
            const activeRefundData = getSessionData(`${widgetNameSpace}-checkoutActiveRefund`);
            resolve({ status: true, activeRefundProducts: activeRefundData.products });
          } else {
            resolve({ status: false });
          }
        });
      }
    });
  }, [ optDataState, qualifyActiveRefundOffer, isMatchedActiveRefundOffer, widgetNameSpace ]);

  const handleValidate = useCallback(() => {
    return new Promise((resolve) => {
      if (isMatchedActiveRefundOffer) {
        renderActiveRefund.CommerceObserver.emit('validate', {
          callback: ({ status }) => {
            resolve(status === 'success');
          },
        });
      } else {
        resolve(true);
      }
    });
  }, [ isMatchedActiveRefundOffer, renderActiveRefund ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-offer-reInit`, reInitActiveRefund);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-offer-reInit`, reInitActiveRefund);
    };
  }, [ widgetNameSpace, handleQualify ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-offer-qualify`, handleQualify);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-offer-qualify`, handleQualify);
    };
  }, [ widgetNameSpace, handleQualify ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-validate-block`, handleValidate);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-validate-block`, handleValidate);
    };
  }, [ widgetNameSpace, handleValidate ]);

  useEffect(() => {
    const renderer = mountActiveRefund();
    setRenderActiveRefund(renderer);
    return () => {
      renderer?.destroy();
    };
  }, [ widgetToken, offerRenderer, widgetAPIServerHost, locale ]);

  if (!widgetToken || !offerRenderer) {
    return null;
  }

  return (
    <div className="active-refund-offer" id="offer-widget-activeRefund" />
  );
};

export default memo(ActiveRefund);
